import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { designTokens } from '@app/theme';
import { usePermissions } from '@refinedev/core';
import { Permissions } from '@shared/constants/permissions';
import { parseApiError } from '@shared/lib/api-error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Empty,
  Grid,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Typography,
  notification,
} from 'antd';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { RoleResponse } from '@api/generated/types.gen';

import {
  createRole,
  deleteRole,
  fetchPermissions,
  fetchRoles,
  replaceRolePermissions,
  updateRoleMeta,
} from './api';
import { RoleEditor, type RoleFormValues } from './components/RoleEditor';
import { permissionNamesToIds } from './lib';

const { Title, Text } = Typography;

const ROLES_KEY = ['admin-roles'];
const PERMISSIONS_KEY = ['admin-permissions'];
const MAX_VISIBLE_TAGS = 6;

type EditorState = {
  open: boolean;
  mode: 'create' | 'edit';
  role: RoleResponse | null;
};

/** Истинно, когда два набора id совпадают без учёта порядка. */
function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

/**
 * Раздел «Роли и права»: справочник ролей с созданием, редактированием
 * (имя/описание + набор прав) и удалением.
 *
 * Чтение — право `roles.read`; мутации гейтятся `roles.write` через
 * `usePermissions`. Бэк разделяет обновление метаданных роли и её набора прав
 * на два эндпоинта — при сохранении дёргаем только изменившиеся.
 *
 * Данные тянутся напрямую SDK + react-query (как в `UserShowPage`): набор
 * операций RBAC не ложится на Refine data-provider без костылей.
 */
export function RbacPage() {
  const { t } = useTranslation(['rbac', 'common', 'errors']);
  const qc = useQueryClient();
  const screens = Grid.useBreakpoint();
  const { data: perms } = usePermissions<string[]>({});
  const canWrite = perms?.includes(Permissions.ROLES_WRITE) ?? false;
  const canReadPermissions = perms?.includes(Permissions.PERMISSIONS_READ) ?? false;

  const [editor, setEditor] = useState<EditorState>({ open: false, mode: 'create', role: null });

  const rolesQuery = useQuery({ queryKey: ROLES_KEY, queryFn: fetchRoles });

  const permissionsQuery = useQuery({
    queryKey: PERMISSIONS_KEY,
    queryFn: fetchPermissions,
    enabled: canWrite && canReadPermissions,
  });
  const permissionsCatalog = useMemo(
    () => permissionsQuery.data ?? [],
    [permissionsQuery.data],
  );

  const notifyError = (err: unknown): void => {
    const e = err as { response?: { data?: unknown; status?: number } };
    const apiErr = parseApiError(e.response?.data, e.response?.status);
    notification.error({
      message: t(`errors:${apiErr.code}`, { defaultValue: t('errors:UNKNOWN_ERROR') }),
      placement: 'topRight',
    });
  };

  const closeEditor = (): void => {
    setEditor((prev) => ({ ...prev, open: false }));
  };

  const saveMutation = useMutation({
    mutationFn: async (vars: {
      mode: 'create' | 'edit';
      role: RoleResponse | null;
      values: RoleFormValues;
    }): Promise<{ changed: boolean; mode: 'create' | 'edit' }> => {
      const description = vars.values.description || undefined;
      if (vars.mode === 'create') {
        await createRole({
          name: vars.values.name,
          description,
          permissionIds: vars.values.permissionIds,
        });
        return { changed: true, mode: 'create' };
      }
      const id = vars.role?.id ?? '';
      const metaChanged =
        vars.values.name !== (vars.role?.name ?? '') ||
        vars.values.description !== (vars.role?.description ?? '');
      const currentIds = permissionNamesToIds(vars.role?.permissions ?? [], permissionsCatalog);
      const permsChanged = !sameIdSet(currentIds, vars.values.permissionIds);
      if (!metaChanged && !permsChanged) {
        return { changed: false, mode: 'edit' };
      }
      if (metaChanged) {
        await updateRoleMeta(id, { name: vars.values.name, description });
      }
      if (permsChanged) {
        await replaceRolePermissions(id, vars.values.permissionIds);
      }
      return { changed: true, mode: 'edit' };
    },
    onSuccess: (result) => {
      if (!result.changed) {
        notification.info({ message: t('rbac:editor.unchanged'), placement: 'topRight' });
        closeEditor();
        return;
      }
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
      notification.success({
        message: result.mode === 'create' ? t('rbac:editor.created') : t('rbac:editor.updated'),
        placement: 'topRight',
      });
      closeEditor();
    },
    onError: notifyError,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRole(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
      notification.success({ message: t('rbac:delete.deleted'), placement: 'topRight' });
    },
    onError: notifyError,
  });

  const handleSubmit = (values: RoleFormValues): void => {
    saveMutation.mutate({ mode: editor.mode, role: editor.role, values });
  };

  const renderPermissions = (names: string[] | undefined): ReactNode => {
    const list = names ?? [];
    if (list.length === 0) {
      return (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('rbac:noPermissions')}
        </Text>
      );
    }
    const shown = list.slice(0, MAX_VISIBLE_TAGS);
    const rest = list.length - shown.length;
    return (
      <Space wrap size={4}>
        {shown.map((name) => (
          <Tag
            key={name}
            style={{
              fontSize: 11,
              fontFamily: '"JetBrains Mono", monospace',
              margin: 0,
            }}
          >
            {name}
          </Tag>
        ))}
        {rest > 0 && (
          <Tag style={{ fontSize: 11, margin: 0, fontWeight: 600 }}>{`+${String(rest)}`}</Tag>
        )}
      </Space>
    );
  };

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div>
          <Title level={3} style={{ marginBlockEnd: 4, fontWeight: 600 }}>
            {t('rbac:title')}
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            {t('rbac:subtitle')}
          </Text>
        </div>
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditor({ open: true, mode: 'create', role: null });
            }}
          >
            {t('rbac:create')}
          </Button>
        )}
      </div>

      <Card style={{ borderColor: designTokens.slate200 }} styles={{ body: { padding: 0 } }}>
        <Spin spinning={rolesQuery.isLoading}>
          {!rolesQuery.isLoading && (rolesQuery.data ?? []).length === 0 ? (
            <div style={{ padding: 32 }}>
              <Empty description={t('rbac:empty')} />
            </div>
          ) : (
            (rolesQuery.data ?? []).map((role, idx) => (
              <div
                key={role.id}
                style={{
                  padding: '12px 16px',
                  borderBlockStart: idx > 0 ? `1px solid ${designTokens.slate100}` : 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <Tag
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: 0.3,
                      fontFamily: '"JetBrains Mono", monospace',
                      marginBlockStart: 2,
                    }}
                  >
                    {role.name}
                  </Tag>
                  {canWrite && (
                    <Space size={4} style={{ flexShrink: 0 }}>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        aria-label={t('common:actions.edit')}
                        onClick={() => {
                          setEditor({ open: true, mode: 'edit', role });
                        }}
                      >
                        {screens.md ? t('common:actions.edit') : null}
                      </Button>
                      <Popconfirm
                        title={t('rbac:delete.confirmTitle')}
                        description={t('rbac:delete.confirmDescription')}
                        okText={t('common:actions.delete')}
                        cancelText={t('common:actions.cancel')}
                        okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
                        onConfirm={() => {
                          deleteMutation.mutate(role.id ?? '');
                        }}
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label={t('rbac:delete.action')}
                        >
                          {screens.md ? t('common:actions.delete') : null}
                        </Button>
                      </Popconfirm>
                    </Space>
                  )}
                </div>

                <div style={{ marginBlockStart: 4 }}>
                  {role.description ? (
                    <Text style={{ fontSize: 13, color: designTokens.slate500 }}>
                      {role.description}
                    </Text>
                  ) : (
                    <Text style={{ fontSize: 13, color: designTokens.slate400 }}>
                      {t('rbac:noDescription')}
                    </Text>
                  )}
                </div>

                <div style={{ marginBlockStart: 8 }}>{renderPermissions(role.permissions)}</div>
              </div>
            ))
          )}
        </Spin>
      </Card>

      <RoleEditor
        open={editor.open}
        mode={editor.mode}
        role={editor.role}
        permissions={permissionsCatalog}
        permissionsLoading={permissionsQuery.isLoading}
        saving={saveMutation.isPending}
        onCancel={closeEditor}
        onSubmit={handleSubmit}
      />
    </Space>
  );
}
